import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { IconLoader2 } from '@tabler/icons-react';

import { cn } from '@/lib/utils';

/**
 * Rook button: two axes rather than one list. `variant` is how loud the surface
 * is, `accent` is what colour it carries. Splitting them is what stops
 * "destructive" and "ghost" competing for the same slot.
 *
 * primary   filled — the one real action on a screen
 * secondary bordered, transparent fill — the common case
 * tertiary  flat, materialises on hover — toolbars and dense rows
 */
const buttonVariants = cva(
  [
    'inline-flex shrink-0 items-center justify-center gap-1.5 whitespace-nowrap',
    'rounded-md border border-transparent font-medium',
    'cursor-pointer transition-[background-color,border-color,color] duration-100',
    'outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/40',
    'disabled:pointer-events-none disabled:text-fg-extra-light',
    "[&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
  ],
  {
    variants: {
      variant: {
        primary: '',
        secondary: 'border-line-strong',
        tertiary: '',
      },
      accent: { ink: '', green: '', danger: '' },
      size: {
        xs: 'h-6 px-2 text-sm',
        sm: 'h-7 px-2.5 text-base',
        md: 'h-8 px-3 text-base',
        'icon-xs': 'size-6',
        'icon-sm': 'size-7',
        icon: 'size-8',
      },
    },
    compoundVariants: [
      {
        variant: 'primary',
        accent: 'ink',
        class: 'bg-primary text-primary-foreground hover:bg-primary-hover',
      },
      {
        variant: 'primary',
        accent: 'green',
        class: 'bg-success text-success-foreground hover:bg-success-hover',
      },
      {
        variant: 'primary',
        accent: 'danger',
        class: 'bg-destructive text-white hover:bg-destructive-hover',
      },
      {
        variant: 'primary',
        class: 'disabled:bg-wash-light disabled:text-fg-extra-light',
      },

      {
        variant: 'secondary',
        accent: 'ink',
        class: 'bg-wash-lighter text-fg-secondary hover:bg-wash-light',
      },
      {
        variant: 'secondary',
        accent: 'green',
        class: 'border-success text-success hover:bg-success-wash',
      },
      {
        variant: 'secondary',
        accent: 'danger',
        class: 'border-destructive text-destructive hover:bg-destructive-wash',
      },

      {
        variant: 'tertiary',
        accent: 'ink',
        class: 'text-fg-secondary hover:bg-wash-light hover:text-fg',
      },
      {
        variant: 'tertiary',
        accent: 'green',
        class: 'text-success hover:bg-success-wash',
      },
      {
        variant: 'tertiary',
        accent: 'danger',
        class: 'text-destructive hover:bg-destructive-wash',
      },
    ],
    defaultVariants: { variant: 'primary', accent: 'ink', size: 'sm' },
  }
);

function Button({
  className,
  variant,
  accent,
  size,
  asChild = false,
  isLoading = false,
  disabled,
  children,
  ...props
}: React.ComponentProps<'button'> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean;
    isLoading?: boolean;
  }) {
  const Comp = asChild ? Slot : 'button';

  return (
    <Comp
      data-slot="button"
      className={cn(buttonVariants({ variant, accent, size, className }))}
      disabled={disabled || isLoading}
      {...props}
    >
      {asChild ? (
        children
      ) : (
        <>
          {isLoading && <IconLoader2 className="animate-spin" />}
          {children}
        </>
      )}
    </Comp>
  );
}

export { Button, buttonVariants };
